import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useUser } from "./UserContext";
import { registerPushNotifications, setupPushListeners } from "../lib/pushNotifications";
import { buildSendNotificationArgs, SEND_NOTIFICATION_RPC } from "../lib/notificationSend";

const NotificationContext = createContext();

const POLL_INTERVAL = 120000; // 2분
const RIGHTS_HOLDER_CACHE_DURATION = 5 * 60 * 1000; // 5분
const rightsHolderCache = new Map();

export function NotificationProvider({ children }) {
  const { userInfo } = useUser();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const pollRef = useRef(null);
  const isVisibleRef = useRef(true);

  const email = userInfo?.email;

  // Fetch unread count
  const refreshCount = useCallback(async () => {
    if (!email) return;
    const { count, error } = await supabase
      .from("notification")
      .select("*", { count: "exact", head: true })
      .eq("recipient_email", email)
      .eq("is_read", false);
    if (!error && count !== null) {
      setUnreadCount(count);
    }
  }, [email]);

  // Fetch notification list (on-demand)
  const fetchNotifications = useCallback(async (category) => {
    if (!email) return;
    let query = supabase
      .from("notification")
      .select("*")
      .eq("recipient_email", email)
      .order("created_at", { ascending: false })
      .limit(30);
    if (category) {
      query = query.eq("category", category);
    }
    const { data } = await query;
    setNotifications(data || []);
  }, [email]);

  // Mark single as read
  const markAsRead = useCallback(async (id) => {
    await supabase
      .from("notification")
      .update({ is_read: true })
      .eq("id", id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async (category) => {
    if (!email) return;
    let query = supabase
      .from("notification")
      .update({ is_read: true })
      .eq("recipient_email", email)
      .eq("is_read", false);
    if (category) {
      query = query.eq("category", category);
    }
    await query;
    setNotifications((prev) =>
      prev.map((n) => {
        if (category && n.category !== category) return n;
        return { ...n, is_read: true };
      })
    );
    if (!category) {
      setUnreadCount(0);
    } else {
      refreshCount();
    }
  }, [email, refreshCount]);

  // Create notification — RPC `send_notification`(SECURITY DEFINER) 경유.
  // 2026-09-05 (QA 1차 대장 #43 후속): 테이블 직접 insert 는 어드민 전용 정책(20260905000002)이라 여기서 하지 않는다.
  // 서버가 type 별 발신자·수신자 관계를 검사하고 비어드민의 actor_name 을 정한다(20260905000001).
  // 호출 형태(입력 키·실패 시 로그만)는 그대로 — 호출처 9곳이 실패를 흐름 중단 사유로 보지 않는다.
  const createNotification = useCallback(async (input) => {
    const { error } = await supabase.rpc(SEND_NOTIFICATION_RPC, buildSendNotificationArgs(input));
    if (error) {
      console.error("Failed to create notification:", error);
    }
  }, []);

  // Helper: get rights holder emails by company
  const getRightsHolderEmails = useCallback(async (company) => {
    if (!company) return [];
    const cached = rightsHolderCache.get(company);
    if (cached && Date.now() - cached.timestamp < RIGHTS_HOLDER_CACHE_DURATION) {
      return cached.emails;
    }
    const { data } = await supabase.rpc("get_rights_holder_emails", {
      p_company: company,
    });
    const emails = (data || []).map((u) => u.email);
    rightsHolderCache.set(company, { emails, timestamp: Date.now() });
    return emails;
  }, []);

  // Push notification registration (native only)
  const navigate = useNavigate();
  const pushInitRef = useRef(false);

  useEffect(() => {
    if (!email || pushInitRef.current) return;
    pushInitRef.current = true;
    registerPushNotifications(email);
    setupPushListeners(navigate, refreshCount);
  }, [email, navigate, refreshCount]);

  // Polling with visibility check
  useEffect(() => {
    if (!email) return;

    refreshCount();

    const handleVisibility = () => {
      isVisibleRef.current = !document.hidden;
      if (!document.hidden) {
        refreshCount();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    pollRef.current = setInterval(() => {
      if (isVisibleRef.current) {
        refreshCount();
      }
    }, POLL_INTERVAL);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [email, refreshCount]);

  const value = useMemo(
    () => ({
      unreadCount,
      notifications,
      fetchNotifications,
      markAsRead,
      markAllAsRead,
      createNotification,
      getRightsHolderEmails,
      refreshCount,
    }),
    [unreadCount, notifications, fetchNotifications, markAsRead, markAllAsRead, createNotification, getRightsHolderEmails, refreshCount]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  return useContext(NotificationContext);
}
